import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, FileText, Video, Download } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { ROLE_LABELS } from "@/lib/constants";
import type { TutorialPdf, TutorialVideo } from "@shared/schema";

interface TutorialTopicoComVideo {
  id: string;
  titulo: string;
  video: TutorialVideo | null;
}

interface TutorialDoPapel {
  papel: string;
  pdf: TutorialPdf | null;
  topicos: TutorialTopicoComVideo[];
}

export default function Tutoriais() {
  const { session, loading } = useAuth();
  const { toast } = useToast();
  const token = session?.access_token ?? "";

  const { data: tutoriais, isLoading } = useQuery<TutorialDoPapel[]>({
    queryKey: ["/api/tutoriais"],
    queryFn: async () => {
      const res = await fetch("/api/tutoriais", { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Erro ao carregar tutoriais");
      return res.json();
    },
    enabled: !!token,
  });

  async function baixarPdf(id: string) {
    try {
      const res = await fetch(`/api/tutoriais/pdfs/${id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Erro ao gerar link do PDF");
      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      toast({ title: (err as Error).message, variant: "destructive" });
    }
  }

  async function baixarVideo(id: string) {
    try {
      const res = await fetch(`/api/tutoriais/videos/${id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Erro ao gerar link do vídeo");
      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      toast({ title: (err as Error).message, variant: "destructive" });
    }
  }

  if (loading || isLoading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-display text-slate-900">Tutoriais</h1>
        <p className="text-muted-foreground mt-1">
          Materiais de apoio para o seu papel no sistema
        </p>
      </div>

      {!tutoriais || tutoriais.length === 0 ? (
        <Card className="border-none shadow-md max-w-2xl">
          <CardContent className="py-12 text-center text-slate-500">
            <FileText className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">Nenhum tutorial disponível ainda</p>
          </CardContent>
        </Card>
      ) : (
        <div className="max-w-2xl space-y-6">
          {tutoriais.map((item) => (
            <Card key={item.papel} className="border-none shadow-md">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-slate-900">
                  {ROLE_LABELS[item.papel] ?? item.papel}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between border rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-slate-500" />
                    {item.pdf ? `PDF v${item.pdf.versao}` : "Nenhum PDF disponível ainda"}
                  </div>
                  {item.pdf && (
                    <Button size="sm" variant="outline" onClick={() => baixarPdf(item.pdf!.id)}>
                      <Download className="w-3.5 h-3.5 mr-1.5" /> Baixar PDF
                    </Button>
                  )}
                </div>

                {item.topicos.length === 0 ? (
                  <p className="text-xs text-slate-500">Nenhum tópico de vídeo cadastrado ainda.</p>
                ) : (
                  <div className="space-y-2">
                    {item.topicos.map((topico) => (
                      <div key={topico.id} className="flex items-center justify-between border rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Video className="w-4 h-4 text-slate-500" />
                          {topico.titulo}
                        </div>
                        {topico.video ? (
                          <Button size="sm" variant="outline" onClick={() => baixarVideo(topico.video!.id)}>
                            <Download className="w-3.5 h-3.5 mr-1.5" /> Assistir vídeo
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-400 italic">
                            Vídeos estão sendo feitos, logo mais disponível
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </Shell>
  );
}
